import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { AdaptationType } from "@/types/adaptation";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import AdaptationTag from "@/components/display/AdaptationTag";
import { getTheme } from "@/features/branding";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { AsteriskIcon } from "@phosphor-icons/react";
import { ConfigProvider, InputNumber, Slider, Space } from "antd";
import { useCollectionState } from "../context/CollectionContext";
import { CompareResultsMode } from "../types";
import CollectionFiltersLabel from "./CollectionFiltersLabel";
import { BreakEvenInvestmentChart } from "./charts/BreakEvenInvestmentChart";
import { Stats } from "./charts/Stats";
import { Header } from "./charts/common/Text";
import { computeAvoidedRiskMetrics } from "./utils/adaptationRoi";
type AdaptationInsightsProps = {
  id: string;
  baselineSummary: PeaSummaryResponse;
  comparisonSummary: PeaSummaryResponse;
  comparisonType: AdaptationType;
  isLargeSite: boolean;
  showDescriptionContext?: boolean;
  compareResultsMode: CompareResultsMode;
  translationPrefix?: string;
  isPointLevel?: boolean;
};
const DEFAUT_DISCOUNT_RATE = 5;
const BASE_YEAR = 2025;
const discountSliderDefaults = {
  min: 0,
  max: 10,
  step: 0.01
};
const ResilienceFinancialInsights = ({
  id,
  baselineSummary,
  comparisonSummary,
  comparisonType,
  isLargeSite,
  compareResultsMode,
  isPointLevel = false
}: AdaptationInsightsProps) => {
  const {
    t: tAdaptationInsights
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.adaptationInsights"
  });
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: "portfolios.portfolioLevel.hazardBreakdown"
  });
  const {
    t: terms
  } = useTranslation("terms");
  const {
    antd
  } = getTheme();
  const colorPrimary = antd.token?.colorPrimary;
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite
  });
  const {
    rcp,
    year,
    hazard
  } = useCollectionState();
  const [discountRate, setDiscountRate] = useState<number>(DEFAUT_DISCOUNT_RATE);
  const insights = useMemo(() => computeAvoidedRiskMetrics({
    baselineSummary,
    comparisonSummary,
    rcp,
    hazard,
    selectedYear: year,
    discountRate,
    baseYear: BASE_YEAR
  }), [baselineSummary, comparisonSummary, rcp, year, hazard, discountRate]);
  if (!canRunAutoAdaptation) return null;
  const isPastYear = year < BASE_YEAR;
  const pastDataNotApplicable = <div className="flex items-center justify-center px-3 py-6 text-center text-sm text-gray-400">
      Past data not applicable.
    </div>;
  const tag = canRunAutoAdaptation && <AdaptationTag type={compareResultsMode === "compare" ? "enhanced_resilience" : compareResultsMode} />;
  return <div id={id} className="grid grid-cols-12 gap-0 @container/wrapper" data-sentry-component="ResilienceFinancialInsights" data-sentry-source-file="ResilienceFinancialInsights.tsx">
      <div className="col-span-12 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Header title={tAdaptationInsights("title") ?? ""} data-sentry-element="Header" data-sentry-source-file="ResilienceFinancialInsights.tsx" />
          {tag}
        </div>

        <ContextualInfoCollapse data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="ResilienceFinancialInsights.tsx">
          {canRunAutoAdaptation && compareResultsMode ? <Trans i18nKey={tAdaptationInsights(`description`)} values={{
          baseline: terms("adaptationTypes.baseline"),
          enhancedResilience: terms(`adaptationTypes.${compareResultsMode === "compare" ? "enhanced_resilience" : compareResultsMode}`)
        }} components={{
          span: <strong />
        }} /> : tAdaptationInsights("description")}
        </ContextualInfoCollapse>
      </div>
      <div id={id} className="col-span-12 mt-4">
        <div className="grid grid-cols-1 items-stretch gap-2 pb-4">
          {hazard !== "total" ? <div className="col-span-full">
              <Stats label={tHazardBreakdown("unavailableMessage")} value={""} footer={<div className="mt-2">
                    <CollectionFiltersLabel />
                  </div>} />
            </div> : <>
              <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-surface p-3 shadow-sm">
                <ContextualInfoButtonWithText rootClassName="mb-2 text-base font-semibold" id="avoidedRiskValue">
                  {tAdaptationInsights("valueStats.avoidedRisk.label") ?? ""}
                </ContextualInfoButtonWithText>

                {isPastYear ? pastDataNotApplicable : <div className="grid flex-1 grid-cols-2 items-stretch gap-2">
                    <Stats label={`${tAdaptationInsights("valueStats.avoidedRisk.annual.label")} (${insights.periods.selectedYear})`} value={insights.annualAvoidedRisk} footer={<>
                          {tAdaptationInsights("valueStats.avoidedRisk.annual.description", {
                  selectedYear: insights.periods.selectedYear
                })}
                          <div className="mt-2">
                            <CollectionFiltersLabel />
                          </div>
                        </>} unit="$" unitPosition="start" tag={tag} />
                    <Stats label={`${tAdaptationInsights("valueStats.avoidedRisk.cumulative.label")} (${insights.periods.startYear} - ${insights.periods.selectedYear})`} value={insights.cumulativeAvoidedRisk} footer={<>
                          {tAdaptationInsights("valueStats.avoidedRisk.cumulative.description", {
                  startYear: insights.periods.startYear,
                  selectedYear: insights.periods.selectedYear
                })}{" "}
                          <div className="mt-2">
                            <CollectionFiltersLabel />
                          </div>
                        </>} unit="$" unitPosition="start" tag={tag} />
                  </div>}
              </div>
            </>}
        </div>

        <Space size={16} orientation="vertical" className="w-full" data-sentry-element="Space" data-sentry-source-file="ResilienceFinancialInsights.tsx">
          <div className="row-span-2">
            <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-surface p-3 shadow-sm">
              <ContextualInfoButtonWithText rootClassName="mb-2 text-base font-semibold" id="breakevenInvestment" data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="ResilienceFinancialInsights.tsx">
                {tAdaptationInsights("valueStats.breakevenInvestment.label") ?? ""}
              </ContextualInfoButtonWithText>

              {isPastYear ? pastDataNotApplicable : <div className="grid flex-1 grid-cols-2 items-stretch gap-2">
                  <Stats label={`${tAdaptationInsights("valueStats.breakevenInvestment.cumulative.label")} (${insights.periods.startYear} - ${insights.periods.breakEvenEndYear})`} value={insights.cumulativeBreakEvenInvestment} footer={<>
                        {tAdaptationInsights("valueStats.breakevenInvestment.cumulative.description", {
                  startYear: insights.periods.startYear,
                  endYear: insights.periods.breakEvenEndYear
                })}
                        <div className="mt-2">
                          <CollectionFiltersLabel />
                        </div>
                      </>} unit="$" unitPosition="start" tag={tag} />
                  <div className="rounded-xl border border-gray-200 bg-surface p-3 @4xl:p-4">
                    <div className="flex items-center justify-between gap-1">
                      <div className="text-sm font-medium text-gray-900">
                        {tAdaptationInsights("valueStats.discountRate.label")}
                      </div>
                      {tag}
                    </div>
                    <ConfigProvider theme={{
                  components: {
                    Slider: {
                      trackBg: colorPrimary
                    }
                  }
                }}>
                      <div className="flex flex-col items-end justify-end gap-2 px-3">
                        <Slider className="w-full" {...discountSliderDefaults} marks={{
                      0: `${discountSliderDefaults.min}%`,
                      5: `${discountSliderDefaults.max / 2}%`,
                      10: `${discountSliderDefaults.max}%`
                    }} onChange={setDiscountRate} value={discountRate} />
                        <InputNumber {...discountSliderDefaults} value={discountRate} onChange={value => {
                      const parsedValue = value === null || isNaN(value) ? 0 : value.toFixed(2);
                      setDiscountRate(Number(parsedValue));
                    }} suffix="%" precision={2} />
                      </div>
                    </ConfigProvider>
                    <div className="mt-2">
                      <CollectionFiltersLabel />
                    </div>
                  </div>
                  <div className="col-span-2 rounded-xl border border-gray-200 bg-surface p-3 shadow-sm @4xl:p-4">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-12 flex items-start justify-end text-right @4xl/wrapper:col-span-4">
                        <CollectionFiltersLabel withIcons />
                      </div>
                      <div className="col-span-12 mb-2 @4xl/wrapper:order-first @4xl/wrapper:col-span-8">
                        <div className="mb-2 flex items-center justify-between gap-2 text-sm font-medium text-gray-900 @4xl/wrapper:justify-start">
                          {tAdaptationInsights("charts.cumilativeRisk.title")} {tag}
                        </div>
                        <p className="text-sm text-gray-500">
                          {tAdaptationInsights("charts.cumilativeRisk.description") ?? ""}
                        </p>
                      </div>
                    </div>

                    <BreakEvenInvestmentChart baselineSummary={baselineSummary} comparisonSummary={comparisonSummary} comparisonType={comparisonType} rcp={rcp} hazard={hazard} selectedYear={year} discountRate={discountRate} baseYear={BASE_YEAR} isPointLevel={isPointLevel} />
                  </div>
                </div>}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-100 p-3 shadow-sm @4xl:p-4">
            <div className="mb-2 flex items-center gap-1 text-sm font-medium text-gray-900">
              <sup>
                <AsteriskIcon data-sentry-element="AsteriskIcon" data-sentry-source-file="ResilienceFinancialInsights.tsx" />
              </sup>
              {tAdaptationInsights("guide.title")}
            </div>
            <p>{tAdaptationInsights("guide.description")}</p>
          </div>
        </Space>
      </div>
    </div>;
};
export default ResilienceFinancialInsights;